import { useEffect, useRef, useState } from 'react';
import {
  MessageCircleQuestion,
  Mic,
  Square,
  Send,
  Loader2,
  X,
  Trash2,
  RotateCcw,
  Copy,
  Check,
} from 'lucide-react';
import Markdown from './Markdown';
import PanelWidthToggle from './PanelWidthToggle';
import { askQuestion, transcribeAudio } from '../services/lectureRagApi';
import { useLectureStore } from '../store/useLectureStore';
import { useFeature } from '../../../stores/featureStore';

interface AskQuestionPanelProps {
  lessonId: string;
  onClose: () => void;
  /** Panel width, owned by LecturePlayer — the toggle lives in this header
   *  beside Close rather than in a strip of its own. */
  wide?: boolean;
  onToggleWide?: () => void;
}

// Free-form Q&A panel: type or speak a question (MediaRecorder -> /stt -> fills
// the box), submit to /ai/ask, render the grounded answer + citations, and read
// it aloud. Conversation persists in the store's chatHistory.
export default function AskQuestionPanel({ lessonId, onClose, wide, onToggleWide }: AskQuestionPanelProps) {
  const slideIndex = useLectureStore((s) => s.currentSlideIndex);
  const chatHistory = useLectureStore((s) => s.chatHistory);
  const addChatMessage = useLectureStore((s) => s.addChatMessage);
  const clearChat = useLectureStore((s) => s.clearChat);
  const goToSlide = useLectureStore((s) => s.goToSlide);
  const micEnabled = useFeature('live_stt');

  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Keep the conversation scrolled to the latest turn.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [chatHistory, sending]);

  // Stop the mic stream if the panel unmounts mid-recording.
  useEffect(() => {
    return () => {
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== 'inactive') {
        mr.stream.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  // Which answer most recently showed its "copied" confirmation. Keyed by id so
  // copying a second answer does not leave the first one still ticked.
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copyAnswer = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
    } catch {
      // Clipboard denied (insecure context, or the user refused permission).
      // Nothing useful to say — the text is on screen and selectable.
    }
  };

  const submit = async (retryText?: string) => {
    const query = (retryText ?? input).trim();
    if (!query || sending) return;
    // A retry re-sends an existing turn, so it must not clear the composer or
    // append a duplicate copy of the question the user can already see.
    if (retryText == null) {
      setInput('');
      addChatMessage({ role: 'user', content: query, slideIndex });
    }
    setSending(true);
    try {
      const res = await askQuestion({ lessonId, slideIndex, query });
      addChatMessage({
        role: 'assistant',
        content: res.text,
        citations: res.citations,
        slideIndex,
      });
    } catch {
      addChatMessage({
        role: 'assistant',
        content: 'Sorry — I could not answer that just now. Please try again.',
        slideIndex,
      });
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    setMicError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, {
          type: mr.mimeType || 'audio/webm',
        });
        setTranscribing(true);
        try {
          const { text } = await transcribeAudio(blob);
          setInput((prev) => (prev ? `${prev} ${text}` : text));
        } catch {
          setMicError('Transcription failed. Type your question instead.');
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      setMicError('Microphone unavailable. Check browser permissions.');
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
    setRecording(false);
  };

  return (
    <div className="flex h-full flex-col bg-ws-surface">
      <header className="flex items-center justify-between border-b border-ws-border px-4 py-3">
        <div className="flex items-center gap-2">
          <MessageCircleQuestion size={15} className="text-ws-ai" />
          <h2 className="text-sm font-semibold text-ws-ink">Ask a question</h2>
        </div>
        <div className="flex items-center gap-1">
          {onToggleWide && <PanelWidthToggle wide={!!wide} onToggle={onToggleWide} />}
          {chatHistory.length > 0 && (
            <button
              type="button"
              onClick={clearChat}
              className="grid size-9 sm:size-7 place-items-center rounded-md text-ws-ink-3 transition-colors hover:bg-ws-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
              title="Clear conversation"
              aria-label="Clear conversation"
            >
              <Trash2 size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 sm:size-7 place-items-center rounded-md text-ws-ink-3 transition-colors hover:bg-ws-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
            title="Close"
            aria-label="Close question panel"
          >
            <X size={15} />
          </button>
        </div>
      </header>

      <div
        ref={scrollRef}
        className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
        role="log"
        aria-live="polite"
        aria-label="Conversation"
      >
        {chatHistory.length === 0 && !sending && (
          <p className="px-1 text-xs leading-relaxed text-ws-ink-3">
            Ask anything about this lesson. Answers are grounded in the course
            material and cite their sources.
          </p>
        )}

        {chatHistory.map((m) =>
          m.role === 'user' ? (
            <div key={m.id} className="flex items-end justify-end gap-1">
              {/* Retry re-sends this exact question. It lives on the USER turn
                  because that is the thing being re-asked — putting it on the
                  answer would imply regenerating from the same failed context. */}
              <button
                type="button"
                onClick={() => void submit(m.content)}
                disabled={sending}
                aria-label="Ask this again"
                title="Ask this again"
                className="mb-0.5 grid size-6 shrink-0 place-items-center rounded text-ws-ink-3 transition-colors hover:bg-ws-surface-2 hover:text-ws-ink-2 disabled:opacity-40"
              >
                <RotateCcw className="size-3.5" aria-hidden="true" />
              </button>
              <div className="max-w-[85%] break-words [overflow-wrap:anywhere] rounded-lg rounded-br-sm bg-ws-accent px-3 py-2 text-sm text-white">
                {m.content}
              </div>
            </div>
          ) : (
            <div key={m.id} className="flex flex-col gap-1.5">
              <div className="max-w-[92%] break-words [overflow-wrap:anywhere] rounded-lg rounded-bl-sm bg-ws-surface-2 px-3 py-2.5">
                <Markdown compact>{m.content}</Markdown>
                {m.citations && m.citations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1 border-t border-ws-border pt-2">
                    {m.citations.map((c, i) => {
                      // Tolerate legacy bare-string citations (just a chunk id).
                      if (typeof c === 'string') {
                        return (
                          <span
                            key={c}
                            title={c}
                            className="rounded bg-ws-ai-pale px-1.5 py-0.5 text-[10px] font-medium text-ws-ai-deep"
                          >
                            [{i + 1}]
                          </span>
                        );
                      }
                      const tooltip = `${c.heading ?? 'This lesson'} — ${c.snippet}`;
                      // Slide chunks link to their source slide; lesson-level
                      // chunks have no slide to jump to, so stay a plain span.
                      if (c.slideIndex != null) {
                        return (
                          <button
                            key={c.id}
                            type="button"
                            title={tooltip}
                            onClick={() => goToSlide(c.slideIndex as number)}
                            className="cursor-pointer rounded bg-ws-ai-pale px-1.5 py-0.5 text-[10px] font-medium text-ws-ai-deep transition-colors hover:bg-ws-ai-solid hover:text-ws-on-ai focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
                          >
                            [{i + 1}]
                          </button>
                        );
                      }
                      return (
                        <span
                          key={c.id}
                          title={tooltip}
                          className="rounded bg-ws-ai-pale px-1.5 py-0.5 text-[10px] font-medium text-ws-ai-deep"
                        >
                          [{i + 1}]
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Copy only. Read-aloud was removed from the answer: the lesson
                  already has narration, so a second, differently-voiced reader
                  competing with it was noise rather than a feature. */}
              <button
                type="button"
                onClick={() => void copyAnswer(m.id, m.content)}
                aria-label={copiedId === m.id ? 'Answer copied' : 'Copy answer'}
                title={copiedId === m.id ? 'Copied' : 'Copy answer'}
                className="ml-1 grid size-6 w-fit place-items-center rounded text-ws-ink-3 transition-colors hover:bg-ws-surface-2 hover:text-ws-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
              >
                {copiedId === m.id
                  ? <Check className="size-3.5 text-ws-ok" aria-hidden="true" />
                  : <Copy className="size-3.5" aria-hidden="true" />}
              </button>
            </div>
          )
        )}

        {sending && (
          <div role="status" className="flex items-center gap-2 text-sm text-ws-ink-3">
            <Loader2 size={14} className="animate-spin text-ws-ai" />
            Searching the course material…
          </div>
        )}
      </div>

      {micError && (
        <div role="alert" className="border-t border-ws-border px-4 py-1.5 text-[11px] text-ws-danger">
          {micError}
        </div>
      )}

      <footer className="border-t border-ws-border p-3">
        <div className="flex items-end gap-2">
          {micEnabled && (
            <button
              type="button"
              onClick={recording ? stopRecording : startRecording}
              disabled={transcribing}
              aria-pressed={recording}
              aria-label={recording ? 'Stop recording' : 'Ask by voice'}
              className={`grid size-10 sm:size-9 shrink-0 place-items-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent disabled:opacity-50 ${
                recording
                  ? 'animate-pulse border-ws-danger bg-ws-danger-solid text-ws-on-danger'
                  : 'border-ws-border bg-ws-surface text-ws-ink-2 hover:bg-ws-surface-2'
              }`}
              title={recording ? 'Stop recording' : 'Ask by voice'}
            >
              {transcribing ? (
                <Loader2 size={16} className="animate-spin" />
              ) : recording ? (
                <Square size={15} />
              ) : (
                <Mic size={16} />
              )}
            </button>
          )}

          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            rows={1}
            aria-label="Type a question"
            placeholder={transcribing ? 'Transcribing…' : 'Type a question…'}
            className="max-h-28 min-h-[36px] min-w-0 flex-1 resize-none rounded-md border border-ws-border bg-ws-surface px-3 py-2 text-sm text-ws-ink placeholder:text-ws-ink-4 focus:border-ws-accent focus:outline-none"
          />

          <button
            type="button"
            onClick={() => void submit()}
            disabled={!input.trim() || sending}
            className="grid size-10 sm:size-9 shrink-0 place-items-center rounded-md bg-ws-accent text-white transition-colors hover:bg-ws-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent focus-visible:ring-offset-1 disabled:opacity-40"
            title="Send"
            aria-label="Send question"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </footer>
    </div>
  );
}
